const padMonthOrDay = (digits) => {
  if (!digits) return "";
  return digits.length === 1 ? `0${digits}` : digits;
};

const escapeSqlLiteral = (value) => value.replace(/'/g, "''");
const removeNonDigits = (value) => value.replace(/\D/g, "");
const hasDateSeparator = (value) => /[/.-]/.test(value);

const MIN_YEAR = 1900;
const maxYear = () => new Date().getFullYear();

// A 4-digit number that reads as a plausible birth year ("1985") must NOT be
// coerced into a partial date like "19/85" (month 19). Likewise a real MM/DD
// must reject an impossible day. Validate each segment by the position it would
// occupy in MM/DD/YYYY: 4-digit segment = year (range-checked), first 2-digit
// segment = month, second = day.
const areDatePartsValid = (parts) => {
  for (let i = 0; i < parts.length; i += 1) {
    const part = parts[i];
    if (!part) continue;
    const num = Number(part);
    if (Number.isNaN(num)) return false;
    if (part.length === 4) {
      if (num < MIN_YEAR || num > maxYear()) return false;
    } else if (i === 0) {
      if (num < 1 || num > 12) return false;
    } else if (i === 1) {
      if (num < 1 || num > 31) return false;
    }
  }
  return true;
};

const normalizeDateInput = (input) => {
  const trimmed = String(input).trim();
  if (!trimmed || !hasDateSeparator(trimmed)) return null;

  const compactValue = trimmed.replace(/\s+/g, "");
  const slashValue = compactValue.replace(/[.-]/g, "/");
  const parts = slashValue.split("/");

  const normalizedParts = parts.map((part, index) => {
    const digits = removeNonDigits(part);
    if (!digits) return "";
    if (index < 2 && digits.length <= 2) return padMonthOrDay(digits);
    return digits;
  });

  if (!areDatePartsValid(normalizedParts)) return null;

  const joinedWithSlash = normalizedParts.join("/");
  const slashPattern = joinedWithSlash.replace(/\/+$/g, "");
  if (!slashPattern) return null;

  const hyphenPattern = slashPattern.replace(/\//g, "-");
  const digitSequence = normalizedParts.join("");
  const segmentsWithDigits = normalizedParts.filter((segment) => segment).length;
  const hasFourDigitSegment = normalizedParts.some((segment) => segment.length === 4);

  const isExact = segmentsWithDigits >= 3 && hasFourDigitSegment;
  const allowExactComparison = isExact && /^\d{2}\/\d{2}\/\d{4}$/.test(slashPattern);

  return {
    type: isExact ? "dobExact" : "dobPartial",
    slashPattern,
    hyphenPattern,
    digitSequence,
    allowExactComparison,
  };
};

const createDateTokenFromDigits = (digits) => {
  if (!digits) return null;

  if (digits.length === 6) {
    const candidate = normalizeDateInput(`${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`);
    if (candidate) {
      return { ...candidate, type: "dobPartial" };
    }
  }

  if (digits.length === 8) {
    const mdy = normalizeDateInput(`${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`);
    if (mdy) return mdy;

    const ymd = normalizeDateInput(`${digits.slice(0, 4)}/${digits.slice(4, 6)}/${digits.slice(6)}`);
    if (ymd) return ymd;
  }

  return null;
};

const createDateTokenFromNumericSegments = (segments, startIndex) => {
  const collected = [];
  let cursor = startIndex;

  while (cursor < segments.length && /^\d+$/.test(segments[cursor]) && collected.length < 3) {
    collected.push(segments[cursor]);
    cursor += 1;
  }

  if (collected.length < 2) return null;

  const digitSequence = collected.join("");
  const digitsCandidate = createDateTokenFromDigits(digitSequence);
  if (digitsCandidate) {
    return { token: digitsCandidate, nextIndex: startIndex + collected.length };
  }

  if (collected.length === 2) {
    const delimited = normalizeDateInput(`${collected[0]}/${collected[1]}`);
    if (delimited) {
      return { token: delimited, nextIndex: startIndex + 2 };
    }
  }

  if (collected.length === 3) {
    const mdy = normalizeDateInput(`${collected[0]}/${collected[1]}/${collected[2]}`);
    if (mdy) {
      return { token: mdy, nextIndex: startIndex + 3 };
    }

    const ymd = normalizeDateInput(`${collected[0]}-${collected[1]}-${collected[2]}`);
    if (ymd) {
      return { token: ymd, nextIndex: startIndex + 3 };
    }
  }

  return null;
};

// A bare number is intrinsically ambiguous: "1985" is most likely a birth year,
// "12345" a FIN or MRN, "12051985" a no-separator DOB. Instead of guessing one
// interpretation, emit every plausible candidate so the query can OR them
// together and the UI can tell the user how it was read.
const createNumberToken = (digits) => {
  if (!digits || !/^\d+$/.test(digits)) return null;

  const token = { type: "number", digits };

  const dateToken = createDateTokenFromDigits(digits);
  if (dateToken) token.date = dateToken;

  if (digits.length === 4) {
    const year = Number(digits);
    if (year >= MIN_YEAR && year <= maxYear()) token.year = digits;
  }

  if (digits.length >= 4) token.identifier = digits;

  if (!token.date && !token.year && !token.identifier) return null;
  return token;
};

const normalizeNameValue = (value) =>
  String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/['\u2019`]/g, "");

const addNameToken = (tokens, value) => {
  const lowerCaseValue = String(value).toLowerCase();
  tokens.push({
    type: "name",
    normalizedValue: normalizeNameValue(lowerCaseValue),
    originalValue: lowerCaseValue,
  });
};

export const parseChatSearchTerm = (term) => {
  const trimmed = (term ?? "").trim().replace(/,/g, " ");
  if (!trimmed) {
    return { tokens: [] };
  }

  const tokens = [];
  const segments = trimmed.split(/\s+/);
  // A name with particles ("De La Rosa") arrives as multiple segments but may be
  // stored concatenated or hyphenated. Carry the joined form so the builder can
  // also match "DeLaRosa" / "De-La-Rosa".
  const nameSegments = [];

  for (let index = 0; index < segments.length; index += 1) {
    const segment = segments[index];
    if (!segment) continue;

    const combined = createDateTokenFromNumericSegments(segments, index);
    if (combined) {
      tokens.push(combined.token);
      index = combined.nextIndex - 1;
      continue;
    }

    if (/^\d+$/.test(segment)) {
      const numberToken = createNumberToken(segment);
      if (numberToken) {
        tokens.push(numberToken);
      } else {
        addNameToken(tokens, segment);
        nameSegments.push(segment);
      }
      continue;
    }

    // FIN format: 1-2 digits, colon, then 3+ digits (e.g. "7:110944268", "74:0923").
    // The 3-digit minimum keeps clock times like "7:30" / "12:45" from being
    // treated as an identifier and matching every FIN containing those digits.
    if (/^\d{1,2}:\d{3,}$/.test(segment)) {
      tokens.push({ type: "number", digits: segment, identifier: segment });
      continue;
    }

    const dateToken = normalizeDateInput(segment);
    if (dateToken) {
      tokens.push(dateToken);
      continue;
    }

    addNameToken(tokens, segment);
    nameSegments.push(segment);
  }

  if (nameSegments.length > 1) {
    return { tokens, nameSegments };
  }

  return { tokens };
};

const buildNameClause = (pattern) => {
  const escaped = escapeSqlLiteral(pattern);
  return ` firstName.toLowerCase() like '%${escaped}%' OR lastName.toLowerCase() like '%${escaped}%' `;
};

export const buildChatSearchFilters = (term) => {
  const parsed = parseChatSearchTerm(term);
  const userFilters = [];
  const encounterFilters = [];
  const nameFilters = [];

  parsed.tokens.forEach((token) => {
    if (token.type === "number") {
      // OR every interpretation together. dob/year live on the user record;
      // FIN lives on the encounter record. The backend treats the user and
      // encounter filters as an OR across the two params, so a single typed
      // number matches a patient DOB OR an encounter FIN.
      // MRN / chat-id matching is intentionally NOT emitted here yet — the
      // organization-chats filter grammar for the top-level chat id is
      // unconfirmed (see chatMatchesSearch, which already covers it client-side).
      // FIN/identifier matching is handled client-side in chatMatchesSearch.
      // The organization-chats endpoint does not support contextVariable filtering
      // for search, so emitting a server filter here returns 0 results.

      const dobClauses = [];
      if (token.date) {
        const slashPattern = escapeSqlLiteral(token.date.slashPattern);
        const hyphenPattern = escapeSqlLiteral(token.date.hyphenPattern);
        dobClauses.push(`dob ilike '%${slashPattern}%'`, `dob ilike '%${hyphenPattern}%'`);
        if (token.date.allowExactComparison) {
          dobClauses.push(`dob = date('${slashPattern}', 'MM/dd/yyyy')`);
        }
      }
      if (token.year) {
        dobClauses.push(`dob ilike '%${escapeSqlLiteral(token.year)}%'`);
      }
      if (dobClauses.length > 0) {
        userFilters.push(`(${dobClauses.join(" OR ")})`);
      }
      return;
    }

    if (token.type === "dobExact" || token.type === "dobPartial") {
      const slashPattern = escapeSqlLiteral(token.slashPattern);
      const hyphenPattern = escapeSqlLiteral(token.hyphenPattern);

      const dobClauses = [`dob ilike '%${slashPattern}%'`, `dob ilike '%${hyphenPattern}%'`];
      if (token.allowExactComparison) {
        dobClauses.push(`dob = date('${slashPattern}', 'MM/dd/yyyy')`);
      }

      userFilters.push(`(${dobClauses.join(" OR ")})`);
      encounterFilters.push(
        `(contextVariables['local:onsetDate'] ilike '%${slashPattern}%' OR contextVariables['local:onsetDate'] ilike '%${hyphenPattern}%')`
      );
      return;
    }

    if (token.type === "name") {
      const patterns = Array.from(new Set([token.normalizedValue, token.originalValue])).filter((pattern) => pattern);
      if (patterns.length > 0) {
        const clauses = patterns.map((pattern) => buildNameClause(pattern));
        nameFilters.push(` ${clauses.join(" OR ")} `);
      }
    }
  });

  const nameGroupClauses = [];

  // Each name token must match (AND across tokens) — "john smith" needs both.
  if (nameFilters.length > 0) {
    nameGroupClauses.push(`( ( ${nameFilters.join(" ) AND  ( ")} ) )`);
  }

  // Particle / hyphenated last names ("De La Rosa") may be stored without the
  // spaces or with hyphens. Offer the joined variants as an OR alternative to
  // the token-by-token match, so "DeLaRosa" / "De-La-Rosa" still match without
  // making the per-token requirement stricter.
  if (parsed.nameSegments && parsed.nameSegments.length > 1) {
    const joined = parsed.nameSegments.join("");
    const hyphenated = parsed.nameSegments.join("-");
    const joinedPatterns = Array.from(
      new Set([normalizeNameValue(joined), joined.toLowerCase(), normalizeNameValue(hyphenated), hyphenated.toLowerCase()])
    ).filter((pattern) => pattern);
    if (joinedPatterns.length > 0) {
      const clauses = joinedPatterns.map((pattern) => buildNameClause(pattern));
      nameGroupClauses.push(`( ${clauses.join(" OR ")} )`);
    }
  }

  if (nameGroupClauses.length > 0) {
    userFilters.push(`( ${nameGroupClauses.join(" OR ")} )`);
  }

  return {
    userFilter: userFilters.join(" and "),
    encounterFilter: encounterFilters.join(" and "),
    hasSearch: parsed.tokens.length > 0,
    parsed,
  };
};

export const chatMatchesSearch = (chat, parsedSearch) => {
  const tokens = parsedSearch?.tokens || [];
  if (tokens.length === 0) return true;

  const firstName = chat.user?.firstName?.toLowerCase() || "";
  const lastName = chat.user?.lastName?.toLowerCase() || "";
  const fullName = `${firstName} ${lastName}`.trim();
  const reverseFullName = `${lastName} ${firstName}`.trim();
  const firstNameNormalized = normalizeNameValue(chat.user?.firstName ?? "");
  const lastNameNormalized = normalizeNameValue(chat.user?.lastName ?? "");
  const fullNameNormalized = `${firstNameNormalized} ${lastNameNormalized}`.trim();
  const reverseFullNameNormalized = `${lastNameNormalized} ${firstNameNormalized}`.trim();
  const chatId = chat.id != null ? String(chat.id).toLowerCase() : "";
  const fin = chat.contextVariables?.["local:fin"];
  const finLowerCase = fin != null ? String(fin).toLowerCase() : "";
  const finDigits = removeNonDigits(finLowerCase);
  const dobRaw = chat.user?.dob ? String(chat.user.dob).trim() : "";
  const dobSlash = dobRaw.toLowerCase().replace(/-/g, "/");
  const dobHyphen = dobSlash.replace(/\//g, "-");
  const dobDigits = removeNonDigits(dobRaw);

  return tokens.every((token) => {
    if (token.type === "name") {
      const value = token.normalizedValue;
      const original = token.originalValue;
      return (
        (firstName && firstName.includes(original)) ||
        (lastName && lastName.includes(original)) ||
        (fullName && fullName.includes(original)) ||
        (reverseFullName && reverseFullName.includes(original)) ||
        (firstNameNormalized && firstNameNormalized.includes(value)) ||
        (lastNameNormalized && lastNameNormalized.includes(value)) ||
        (fullNameNormalized && fullNameNormalized.includes(value)) ||
        (reverseFullNameNormalized && reverseFullNameNormalized.includes(value)) ||
        (chatId && (chatId.includes(original) || chatId.includes(value)))
      );
    }

    if (token.type === "number") {
      const digits = token.digits;
      if (token.identifier) {
        if (finLowerCase && finLowerCase.includes(digits)) return true;
        if (finDigits && finDigits.includes(digits)) return true;
        // For colon-formatted FINs, also match by stripping the colon
        if (finDigits && digits.includes(":") && finDigits.includes(removeNonDigits(digits))) return true;
        if (chatId && chatId.includes(digits)) return true;
      }
      if (token.date) {
        if (dobSlash && token.date.slashPattern && dobSlash.includes(token.date.slashPattern.toLowerCase())) return true;
        if (dobHyphen && token.date.hyphenPattern && dobHyphen.includes(token.date.hyphenPattern.toLowerCase())) return true;
        if (dobDigits && token.date.digitSequence && dobDigits.includes(token.date.digitSequence)) return true;
      }
      if (token.year && dobDigits && dobDigits.includes(token.year)) return true;
      return false;
    }

    if (token.type === "dobExact" || token.type === "dobPartial") {
      const slashPattern = token.slashPattern.toLowerCase();
      const hyphenPattern = token.hyphenPattern.toLowerCase();
      const digitSequence = token.digitSequence;

      return (
        (dobSlash && slashPattern && dobSlash.includes(slashPattern)) ||
        (dobHyphen && hyphenPattern && dobHyphen.includes(hyphenPattern)) ||
        (dobDigits && digitSequence && dobDigits.includes(digitSequence))
      );
    }

    return true;
  });
};

// Normalize a patient DOB to a canonical "MM/DD/YYYY" string for both display and
// comparison. The {org}:Search Patients process returns the DOB in different
// shapes across orgs: most return it as an epoch-MILLISECOND timestamp stored at
// MIDNIGHT UTC (e.g. 955584000000 -> 04/13/2000), some return a "MM/DD/YYYY" or
// ISO "YYYY-MM-DD" string. Always read the date components in UTC so the day never
// shifts back one in a western timezone — that off-by-one is what made a typed
// DOB miss (955584000000 read in local EDT would render 04/12/2000, not 04/13).
const padMDY = (date) => {
  if (Number.isNaN(date.getTime())) return "";
  const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(date.getUTCDate()).padStart(2, "0");
  return `${mm}/${dd}/${date.getUTCFullYear()}`;
};

export const normalizeDobValue = (dob) => {
  if (dob === null || dob === undefined || dob === "") return "";
  const raw = String(dob).trim();

  // Epoch millis (all digits, optional leading "-" for pre-1970 DOBs). The 8-digit
  // floor keeps a bare year like "2000" from being read as 2 seconds past epoch.
  if (/^-?\d{8,}$/.test(raw)) return padMDY(new Date(Number(raw)));

  // ISO YYYY-MM-DD — pin to UTC midnight so the day component doesn't drift.
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return padMDY(new Date(`${raw.slice(0, 10)}T00:00:00Z`));

  // MM/DD/YYYY and friends (M/D/YYYY, dot/dash/space separators) — let the parser
  // pad/validate. Only accept a fully-formed mm/dd/yyyy result.
  const parsed = normalizeDateInput(raw);
  return parsed && /^\d{2}\/\d{2}\/\d{4}$/.test(parsed.slashPattern) ? parsed.slashPattern : "";
};

// Display-lenient companion to normalizeDobValue: normalize when the raw DOB is
// recognizable, otherwise fall back to the raw value (then "") rather than
// blanking it — so a directory/label surface never hides a present-but-unparsed
// DOB. Comparison/matching paths MUST keep using the strict normalizeDobValue
// (patientMatchesSearch relies on its ""-on-no-match); this helper is for
// display/grouping only.
export const dobForDisplay = (dob) => normalizeDobValue(dob) || dob || "";

// The Established-patient picker (ModalNewChat.fetchPatients) reuses this to
// refine the {org}:Search Patients result list by DOB *client-side*. The backend
// process filters DOB server-side for COMPLETE dates and full years; this client
// matcher refines the returned candidates — needed for PARTIAL dates (which are
// not filtered server-side) and to keep FE/BE agreement on what matches.
//
// normalizeDobValue turns the patient's raw DOB (usually epoch millis) into the
// same `MM/DD/YYYY` shape parseChatSearchTerm produces for typed input, so a user
// typing the natural forms — "04/13/2000", "4/13/2000", "04-13-2000", "04/2000",
// "2000" — all match. We intentionally adapt the patient into the chat shape
// WITHOUT an `id` so a numeric/DOB query can't accidentally match on a vertex-id
// substring (chatMatchesSearch matches the chat id for the sidebar; unwanted here).
export const patientMatchesSearch = (patient, parsedSearch) => {
  const tokens = parsedSearch?.tokens || [];
  if (tokens.length === 0) return true;

  return chatMatchesSearch(
    {
      user: {
        firstName: patient?.firstName,
        lastName: patient?.lastName,
        dob: normalizeDobValue(patient?.dob),
      },
      contextVariables: {},
    },
    parsedSearch
  );
};

// The process-backed patient lookups ({org}:Search Patients) take a plain
// string, not the SQL-like filter the sidebar builds. Normalize that string so
// those surfaces behave consistently with the sidebar: collapse whitespace and
// reorder "Last, First" into "First Last".
export const buildPatientSearchText = (term) => {
  const raw = (term ?? "").trim();
  if (!raw) return "";

  let working = raw;
  if (raw.includes(",")) {
    const [first, ...rest] = raw.split(",");
    const remainder = rest.join(" ").trim();
    if (first.trim() && remainder) {
      working = `${remainder} ${first.trim()}`;
    }
  }

  return working.replace(/\s+/g, " ").trim();
};

// True when a patient record carries a real, usable name — i.e. it is NOT the
// synthetic "Anonymous" record and is not entirely nameless. Used by the
// Established-patient search (lightning-bolt quick start) so a follow-up / new
// note can only attach to a named patient. Null-safe by design: a record with
// missing or non-string name fields must be excluded, never throw (String()
// coercion guards both nullish and numeric inputs).
//
// Note: intentionally narrower than the directory/AI-panel filters
// (PatientPanel, usePatientDirectory), which additionally hide test/digit/
// single-letter names. Encounter creation must never hide a real patient, so
// we exclude only anonymous + truly-nameless records here.
export const hasRealPatientName = (user) => {
  const first = String(user?.firstName ?? "").trim();
  const last = String(user?.lastName ?? "").trim();
  if (!first && !last) return false; // truly nameless
  if (last.toLowerCase() === "anonymous") return false; // synthetic anonymous record
  return true;
};
